import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Send, Loader2, LogIn, Inbox, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';
import axios from 'axios';
import { toast } from 'sonner';
import { buildSocialLink, buildVendorEmailHref, buildVendorWhatsAppHref } from '@/lib/vendorContactLinks';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const InquiryForm = ({ vendorId, vendorName, vendor }) => {
  const { user, token, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    subject: '',
    wedding_date: null
  });
  const [loading, setLoading] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [messageSent, setMessageSent] = useState(false);
  const vendorEmailHref = buildVendorEmailHref(vendor?.email, vendorName);
  const vendorWhatsAppHref = buildVendorWhatsAppHref(vendor?.whatsapp || '');
  const directLinks = [
    vendor?.website ? { label: 'Website', href: buildSocialLink('website', vendor.website) } : null,
    vendor?.social_links?.instagram ? { label: 'Instagram', href: buildSocialLink('instagram', vendor.social_links.instagram) } : null,
    vendor?.social_links?.facebook ? { label: 'Facebook', href: buildSocialLink('facebook', vendor.social_links.facebook) } : null,
  ].filter(Boolean);

  // Pre-fill form with user data if logged in
  useEffect(() => {
    if (isAuthenticated && user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || ''
      }));
    }
  }, [isAuthenticated, user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDateSelect = (date) => {
    setFormData(prev => ({ ...prev, wedding_date: date }));
    setCalendarOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isAuthenticated) {
        // Use messaging system for logged-in users
        await axios.post(`${API}/messages`, {
          vendor_id: vendorId,
          subject: formData.subject || `Inquiry about wedding services`,
          message: formData.message,
          wedding_date: formData.wedding_date ? format(formData.wedding_date, 'yyyy-MM-dd') : null
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setMessageSent(true);
        toast.success('Message sent! Check your inbox for replies.');
      } else {
        // Use old inquiry system for guests
        await axios.post(`${API}/inquiries`, {
          vendor_id: vendorId,
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: formData.message,
          wedding_date: formData.wedding_date ? format(formData.wedding_date, 'yyyy-MM-dd') : null
        });

        setMessageSent(true);
        toast.success('Inquiry sent successfully!');
      }

      setFormData({
        name: user?.name || '',
        email: user?.email || '',
        phone: '',
        message: '',
        subject: '',
        wedding_date: null
      });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Success state
  if (messageSent) {
    return (
      <div className="bg-white p-6 rounded-sm border border-stone-100 card-shadow text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="h-8 w-8 text-green-600" />
        </div>
        <h3 className="heading-md mb-2">{isAuthenticated ? 'Message Sent!' : 'Inquiry Sent!'}</h3>
        <p className="text-stone-600 text-sm mb-6">
          {isAuthenticated
            ? `Your message has been sent to ${vendorName}. They'll reply directly to your inbox.`
            : `Your enquiry has been sent to ${vendorName}. The vendor can reply directly to your email, and you can also create a free couple account to keep future messages and planning tools together.`}
        </p>
        {(vendorEmailHref || vendorWhatsAppHref || directLinks.length > 0) && (
          <div className="mb-6 rounded-lg border border-[#D4AF37]/25 bg-[#D4AF37]/10 p-4 text-left" data-testid="inquiry-direct-links-box">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8f6b00] mb-2">Direct vendor links</p>
            <p className="text-sm text-stone-600 mb-3">Because you&apos;ve already sent your first message, you can now use the vendor&apos;s direct contact links too.</p>
            <div className="flex flex-wrap gap-2">
              {vendorEmailHref && (
                <a href={vendorEmailHref} className="rounded-full border border-stone-200 bg-white px-3 py-2 text-sm text-stone-700 hover:border-[#5C0120] hover:text-[#5C0120]" data-testid="inquiry-direct-email-link">
                  Email Vendor
                </a>
              )}
              {vendorWhatsAppHref && (
                <a href={vendorWhatsAppHref} target="_blank" rel="noopener noreferrer" className="rounded-full border border-stone-200 bg-white px-3 py-2 text-sm text-stone-700 hover:border-[#25D366] hover:text-[#25D366]" data-testid="inquiry-direct-whatsapp-link">
                  WhatsApp Vendor
                </a>
              )}
              {directLinks.map((link) => (
                <a key={link.label} href={link.href} target="_blank" rel="noopener noreferrer" className="rounded-full border border-stone-200 bg-white px-3 py-2 text-sm text-stone-700 hover:border-[#5C0120] hover:text-[#5C0120]" data-testid={`inquiry-direct-${link.label.toLowerCase()}-link`}>
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        )}
        <div className="space-y-3">
          {isAuthenticated ? (
            <Link to="/planner?tab=messages">
              <Button className="w-full bg-[#5C0120] hover:bg-[#5C0120]/90">
                <Inbox className="h-4 w-4 mr-2" />
                View My Inbox
              </Button>
            </Link>
          ) : (
            <Link to="/client/auth">
              <Button className="w-full bg-[#5C0120] hover:bg-[#5C0120]/90">
                Create Free Couple Account
              </Button>
            </Link>
          )}
          <Button 
            variant="outline" 
            className="w-full"
            onClick={() => setMessageSent(false)}
          >
            Send Another Message
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-sm border border-stone-100 card-shadow">
      <h3 className="heading-md mb-2">Contact {vendorName}</h3>
      <p className="text-stone-500 text-sm mb-6">Send an inquiry to discuss your wedding needs</p>

      {/* Login prompt for better experience */}
      {!isAuthenticated && (
        <div className="bg-[#5C0120]/5 border border-[#5C0120]/20 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <LogIn className="h-5 w-5 text-[#5C0120] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-[#5C0120]">Register for a better experience</p>
              <p className="text-xs text-stone-600 mt-1">
                Get all your vendor messages in one inbox, plus access to AI wedding planner, notes & more!
              </p>
              <Link to="/client/auth" className="text-xs text-[#D4AF37] font-medium hover:underline mt-2 inline-block">
                Create free account →
              </Link>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Show name/email/phone fields only for guests */}
        {!isAuthenticated && (
          <>
            <div className="space-y-2">
              <Label htmlFor="name">Your Name</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="John & Jane Smith"
                required
                className="input-wedding"
                data-testid="inquiry-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="your@email.com"
                required
                className="input-wedding"
                data-testid="inquiry-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+350 200 00000"
                required
                className="input-wedding"
                data-testid="inquiry-phone"
              />
            </div>
          </>
        )}

        {/* Subject field for logged-in users */}
        {isAuthenticated && (
          <div className="space-y-2">
            <Label htmlFor="subject">Subject</Label>
            <Input
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              placeholder="e.g., Wedding Photography Inquiry"
              className="input-wedding"
              data-testid="inquiry-subject"
            />
          </div>
        )}

        <div className="space-y-2">
          <Label>Wedding Date</Label>
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-start text-left font-normal input-wedding"
                data-testid="inquiry-date-trigger"
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {formData.wedding_date ? (
                  format(formData.wedding_date, 'PPP')
                ) : (
                  <span className="text-stone-400">Select a date</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={formData.wedding_date}
                onSelect={handleDateSelect}
                disabled={(date) => date < new Date()}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="space-y-2">
          <Label htmlFor="message">Message</Label>
          <Textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            placeholder="Tell us about your wedding plans..."
            rows={4}
            required
            className="input-wedding resize-none"
            data-testid="inquiry-message"
          />
        </div>

        <Button
          type="submit"
          className="w-full btn-primary btn-lift"
          disabled={loading}
          data-testid="inquiry-submit"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Send className="h-4 w-4 mr-2" />
          )}
          {isAuthenticated ? 'Send Message' : 'Send Inquiry'}
        </Button>

        {isAuthenticated && (
          <p className="text-xs text-stone-500 text-center">
            Replies will appear in your <Link to="/planner?tab=messages" className="text-[#D4AF37] hover:underline">inbox</Link>
          </p>
        )}
      </form>
    </div>
  );
};

export default InquiryForm;
